import { UAParser } from 'ua-parser-js';
import { logDebug, logWarn } from '@personal-portfolio/common-utils';

// Renaming either key orphans every existing visitor's ID and resets their
// recorded creation date, so treat them as a storage contract.
const USER_ID_STORAGE_KEY = 'analytics_user_id';
const USER_ID_DATE_KEY = 'analytics_user_id_date';

/**
 * Collapses ua-parser-js OS names onto a small fixed vocabulary.
 *
 * The output lands in the user ID string, so the set must stay stable: adding
 * or renaming a value makes new IDs incomparable with historical ones.
 */
function normalizeOS(osName?: string): string {
  if (!osName) return 'unknown';

  const os = osName.toLowerCase();

  const osMap: Record<string, string> = {
    windows: 'windows',
    macos: 'macos',
    'mac os': 'macos',
    'mac os x': 'macos',
    macintosh: 'macos',
    linux: 'linux',
    android: 'android',
    ios: 'ios',
    'iphone os': 'ios',
    'ipad os': 'ios',
  };

  if (osMap[os]) return osMap[os];

  // Substring fallback catches versioned names ("Mac OS X 10.15"); insertion
  // order therefore matters — put more specific keys before their prefixes.
  for (const [key, value] of Object.entries(osMap)) {
    if (os.includes(key)) return value;
  }

  return 'unknown';
}

/** See {@link normalizeOS} — same fixed-vocabulary contract, for browsers. */
function normalizeBrowser(browserName?: string): string {
  if (!browserName) return 'unknown';

  const browser = browserName.toLowerCase();

  const browserMap: Record<string, string> = {
    chrome: 'chrome',
    chromium: 'chrome',
    firefox: 'firefox',
    safari: 'safari',
    edge: 'edge',
    'microsoft edge': 'edge',
    opera: 'opera',
    ie: 'ie',
    'internet explorer': 'ie',
    msie: 'ie',
  };

  if (browserMap[browser]) return browserMap[browser];

  for (const [key, value] of Object.entries(browserMap)) {
    if (browser.includes(key)) return value;
  }

  return 'unknown';
}

/**
 * Never throws: a UAParser failure degrades to "unknown" rather than breaking
 * ID generation, which sits on the analytics hot path.
 */
function detectOS(): string {
  if (typeof window === 'undefined') return 'unknown';

  try {
    const parser = new UAParser();
    const os = parser.getOS();
    return normalizeOS(os.name);
  } catch (error) {
    logWarn('Failed to detect OS:', error);
    return 'unknown';
  }
}

/** See {@link detectOS} — same never-throws contract. */
function detectBrowser(): string {
  if (typeof window === 'undefined') return 'unknown';

  try {
    const parser = new UAParser();
    const browser = parser.getBrowser();
    return normalizeBrowser(browser.name);
  } catch (error) {
    logWarn('Failed to detect browser:', error);
    return 'unknown';
  }
}

/**
 * Country code inferred from the browser locale.
 *
 * This is deliberately the only country source: no IP geolocation call is
 * made (privacy decision — see docs/decisions.md, "Privacy hardening"). The
 * consequence is that the value reports the user's *language* preference, not
 * their location — a US-English locale abroad still reports "US".
 */
function getCountryCodeFromLocale(): string {
  if (typeof window === 'undefined') return 'unknown';

  try {
    // navigator.language reflects the user's chosen UI locale, which is the
    // closest proxy available without a network lookup.
    const locale = navigator.language || navigator.languages?.[0] || 'en-US';
    const parts = locale.split('-');

    if (parts.length >= 2) {
      const countryCode = parts[1].toUpperCase();
      // Guard against extended tags like "zh-Hans-CN", where parts[1] is a
      // script subtag rather than a region.
      if (countryCode.length === 2 && /^[A-Z]{2}$/.test(countryCode)) {
        return countryCode;
      }
    }

    // Bare language tags ("en", "de") carry no region. Rather than dropping the
    // segment, guess the most populous market for that language — an
    // acknowledged approximation, not a lookup.
    const languageMap: Record<string, string> = {
      en: 'US',
      es: 'ES',
      fr: 'FR',
      de: 'DE',
      it: 'IT',
      pt: 'BR',
      zh: 'CN',
      ja: 'JP',
      ko: 'KR',
      ru: 'RU',
    };

    const langCode = parts[0].toLowerCase();
    if (languageMap[langCode]) {
      return languageMap[langCode];
    }

    return 'unknown';
  } catch (error) {
    logWarn('Failed to get country code from locale:', error);
    return 'unknown';
  }
}

/**
 * Named `Sync` to advertise the guarantee callers depend on: country resolution
 * never awaits, because it never leaves the browser.
 */
function getCountryCodeSync(): string {
  return getCountryCodeFromLocale();
}

/**
 * Lowercases and hyphenates the locale tag ("en-US" -> "en-us") because the
 * whole user ID is lowercased downstream; keeping the shapes aligned means the
 * segment reads the same however it was obtained.
 */
function getLanguageId(): string {
  if (typeof window === 'undefined') return 'unknown';

  try {
    const language = navigator.language || navigator.languages?.[0] || 'en-US';
    return language.toLowerCase().replace('_', '-');
  } catch (error) {
    logWarn('Failed to get language ID:', error);
    return 'unknown';
  }
}

/**
 * Local-time YYYY-MM-DD (not ISO/UTC) — the date is a human-readable label
 * inside the user ID, not a timestamp to compare across zones.
 */
function formatDate(date: Date = new Date()): string {
  try {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (error) {
    logWarn('Failed to format date:', error);
    return 'unknown';
  }
}

/**
 * The only source of uniqueness in the ID — every other segment is shared by
 * everyone with the same device/locale on the same day. The lower bound keeps
 * the segment a fixed 5-6 digits so IDs stay visually aligned; the ~1e6 space
 * is adequate for a personal-site traffic volume but is not a UUID.
 */
function generateRandomNumber(): string {
  const min = 10000;
  const max = 999999;
  const randomNum = Math.floor(Math.random() * (max - min + 1)) + min;
  return String(randomNum);
}

/**
 * Builds `country_date_os_browser_language_random`
 * (e.g. `us_2025-03-30_windows_chrome_en-us_12345`).
 *
 * The format is human-readable on purpose so an ID can be eyeballed in the GA4
 * console, which also means it is a coarse fingerprint — keep every segment
 * low-cardinality and never add anything identifying.
 *
 * @param creationDate - First-visit date, so a regenerated ID keeps its
 *   original date segment instead of silently re-dating the visitor.
 */
function generateUserId(creationDate?: Date): string {
  if (typeof window === 'undefined') {
    return `unknown_${formatDate()}_unknown_unknown_unknown_${generateRandomNumber()}`.toLowerCase();
  }

  const countryCode = getCountryCodeSync();
  const date = creationDate ? formatDate(creationDate) : formatDate();
  const os = detectOS();
  const browser = detectBrowser();
  const languageId = getLanguageId();
  const randomNum = generateRandomNumber();

  return `${countryCode}_${date}_${os}_${browser}_${languageId}_${randomNum}`.toLowerCase();
}

/**
 * The persistent anonymous analytics identity for this browser.
 *
 * Fully synchronous: all inputs are local (navigator, localStorage), so callers
 * on hot paths (e.g. the resume form submit) can read it without awaiting.
 *
 * Both the ID and its date must be present to count as an existing identity —
 * a half-written pair is treated as absent and regenerated. When localStorage
 * is unavailable (private mode, blocked storage) the caller still gets a valid
 * ID, but a throwaway one: that visitor is counted fresh on every page load.
 */
export function getUserId(): string {
  if (typeof window === 'undefined') {
    return generateUserId();
  }

  try {
    const existingId = localStorage.getItem(USER_ID_STORAGE_KEY);
    const existingDate = localStorage.getItem(USER_ID_DATE_KEY);

    if (existingId && existingDate) {
      return existingId;
    }

    const creationDate = new Date();
    const newId = generateUserId(creationDate);

    localStorage.setItem(USER_ID_STORAGE_KEY, newId);
    localStorage.setItem(USER_ID_DATE_KEY, creationDate.toISOString());

    return newId;
  } catch (error) {
    logWarn('Failed to access localStorage for user ID:', error);
    return generateUserId();
  }
}

/**
 * Drops the stored identity (GDPR/CCPA right to be forgotten). The next
 * `getUserId()` mints a new ID with today's date — the original first-visit
 * date is not recoverable.
 */
export function clearUserId(): void {
  if (typeof window !== 'undefined') {
    try {
      localStorage.removeItem(USER_ID_STORAGE_KEY);
      localStorage.removeItem(USER_ID_DATE_KEY);
      logDebug('Analytics user ID cleared');
    } catch (error) {
      logWarn('Failed to clear user ID:', error);
    }
  }
}
